import { CustomNode } from '@/types/node-types';

type ActiveConnect = {
  side: 'source' | 'target';
  nodeId: string;
  handleId: string;
} | null;

type ValidationResult = {
  isValid: boolean;
  error?: string;
};

/**
 * Calculates the CSS class name for a handle (port) based on the current drag state.
 * Returns 'handle-highlighted' if the connection is valid, 'handle-dimmed' if invalid,
 * or an empty string if no connection is being dragged.
 */
export function getHandleClassName(params: {
  activeConnect: ActiveConnect;
  nodes: CustomNode[];
  currentNodeId: string | undefined;
  currentHandleId: string | null | undefined;
  handleType: 'source' | 'target';
  validateConnection: (
    sourceNode: CustomNode,
    targetNode: CustomNode,
    sourceHandle: string | null,
    targetHandle: string | null,
  ) => ValidationResult;
}): string {
  const {
    activeConnect,
    nodes,
    currentNodeId,
    currentHandleId,
    handleType,
    validateConnection,
  } = params;

  if (!activeConnect) return '';

  if (!currentNodeId) return '';

  const expectedActiveType = handleType === 'source' ? 'target' : 'source';
  if (activeConnect.side !== expectedActiveType) return 'handle-dimmed';

  let sourceNodeId: string;
  let targetNodeId: string;
  let sourceHandleId: string | null;
  let targetHandleId: string | null;

  if (handleType === 'target') {
    sourceNodeId = activeConnect.nodeId;
    targetNodeId = currentNodeId;
    sourceHandleId = activeConnect.handleId || null;
    targetHandleId = currentHandleId || null;
  } else {
    sourceNodeId = currentNodeId;
    targetNodeId = activeConnect.nodeId;
    sourceHandleId = currentHandleId || null;
    targetHandleId = activeConnect.handleId || null;
  }

  const sourceNode = nodes.find((n) => n.id === sourceNodeId);
  const targetNode = nodes.find((n) => n.id === targetNodeId);

  if (!sourceNode || !targetNode) return 'handle-dimmed';

  const result = validateConnection(
    sourceNode,
    targetNode,
    sourceHandleId,
    targetHandleId,
  );

  // either highlight or dim
  return result.isValid ? 'handle-highlighted' : 'handle-dimmed';
}
